import { forwardRef, HtmlHTMLAttributes, memo, useCallback, useMemo } from "react";
import classNames from "classnames";
import { FormatOptionLabelMeta } from "react-select";
import { useIsMobileView } from "../../../utils/useIsMobileHook";
import { Badge } from "../../Badge";
import { Select } from "../../inputs";
import { ScrollbarPropsType, Scrollbars } from "../../Scrollbars";
import { TabHeaderDescription, useTabsContext, useTabsDescriptionContext } from "../TabsContext";
import { TabHeader, TabHeaderSize, TabHeaderType } from "../TabsHeader";

import styles from "./styles.module.scss";

export type TabsHeaderContainerPropsType = HtmlHTMLAttributes<HTMLDivElement> & {
  tabContainerSize?: TabHeaderSize,
  fullWidth?: boolean,
  scrollbarProps?: ScrollbarPropsType
} & ({
  tabContainerType?: Exclude<TabHeaderType, "underline" | "underlineFilled">
  containerDirection?: "vertical"
} | {
  tabContainerType?: Exclude<TabHeaderType, "line">
  containerDirection?: "horizontal"
})

export const TabsHeaderContainer = memo(forwardRef<HTMLDivElement, TabsHeaderContainerPropsType>((
  {
    children,
    className,
    fullWidth = false,
    scrollbarProps,
    tabContainerType = "primary",
    tabContainerSize = "md",
    containerDirection = "horizontal",
    ...props
  }, ref) => {
  const { activeTabId, setActiveTabId } = useTabsContext();
  const isMobileView = useIsMobileView();
  const tabsDescriptions = useTabsDescriptionContext();
  const selectValue = useMemo(() => tabsDescriptions.find(v => v.id === activeTabId), [ activeTabId, tabsDescriptions ]);
  const onChange = useCallback((v: TabHeaderDescription | null) => setActiveTabId(v!.id!), [ setActiveTabId ]);
  const getOptionValue = useCallback((v: TabHeaderDescription) => v.id!, []);
  const formatOptionLabel = useCallback((v: TabHeaderDescription, formatOptionLabelMeta: FormatOptionLabelMeta<TabHeaderDescription>) => {
    const isActiveOption = formatOptionLabelMeta?.selectValue?.[0]?.id === v.id;
    const showBadge = v.badgeProps && !v.errors?.length;
    const showErrorBadge = !!v.errors?.length;

    return (
      <div style={{ display: "flex", justifyContent: "space-between", width: `${!isActiveOption ? "calc(100% - 20px)" : "100%"}` }}>
        {v.children}
        {showBadge && (
          <Badge {...v.badgeProps} badgeType={"pillColor"} badgeSize={v.tabSize} />
        )}
        {showErrorBadge && (
          <Badge badgeType={"pillOutline"} badgeSize={v.tabSize} badgeColor={"error"}>
            {v.errors?.length}
          </Badge>
        )}
      </div>
    );
  }, []);
  const sortedTabsDescriptions = useMemo(
    () => [ ...tabsDescriptions.map((x, i) => ({ ...x, order: x.order ?? i })) ].sort((a, b) => a.order! - b.order!),
    [ tabsDescriptions ]);

  return (
    <>
      {
        isMobileView
          ? <Select<TabHeaderDescription, false>
            rootContainerProps={{ className: className }}
            options={sortedTabsDescriptions}
            getOptionValue={getOptionValue}
            formatOptionLabel={formatOptionLabel}
            onChange={onChange}
            value={selectValue}
          />
          : <div
            {...props}
            ref={ref}
            className={classNames(
              styles.root,
              className,
              styles[tabContainerType],
              {
                [styles.fullWidth]: fullWidth,
                [styles.vertical]: containerDirection === "vertical",
                [styles.horizontal]: containerDirection === "horizontal",
              },
            )}
          >
            <Scrollbars
              {...scrollbarProps}
              options={{
                ...scrollbarProps?.options,
                scrollbars: {
                  visibility: containerDirection === "vertical" ? "v" : "h",
                  ...scrollbarProps?.options?.scrollbars,
                },
              }}
              className={classNames(scrollbarProps?.className, styles.scrollbar, "os-host-flexbox")}
            >
              <div className={styles.content}>

                {
                  //hotfix for page "jumping"
                  !tabsDescriptions.length &&
                  <span
                    className={classNames(
                      styles.fakeTabHeader,
                      styles[tabContainerSize] as keyof typeof styles
                    )}
                  />
                }
                {
                  tabsDescriptions.map(({ order, ...tabHeaderProps }, index) => (
                    <TabHeader
                      {...tabHeaderProps}
                      key={tabHeaderProps.id}
                      style={{
                        ...tabHeaderProps.style,
                        order: order ?? index,
                      }}
                      tabSize={tabContainerSize}
                      tabType={tabContainerType}
                      current={tabHeaderProps.id === activeTabId}
                      onClick={event => {
                        tabHeaderProps.onClick?.(event);
                        if (!event.defaultPrevented) {
                          setActiveTabId(tabHeaderProps.id!);
                        }
                      }}
                    />
                  ))
                }
              </div>
            </Scrollbars>
          </div>
      }
    </>
  );
}));
